import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StreamAccessManagement } from '@/components/admin/stream-access-management';
import { StreamPlayer } from '@/components/streams';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/layouts/admin-layout';
import { ImageUploadDropzone } from '@/components/admin/image-upload-dropzone';
import { MediaImage } from '@/components/ui/media-image';
import { TagsInput } from '@/components/ui/tags-input';
import { COUNTRIES } from '@/components/ui/region-select';
import { dashboardApi } from '@/lib/services/dashboard-api';
import {
  Play,
  Pause,
  Settings,
  Users,
  BarChart3,
  Video,
  Clock,
  Eye,
  EyeOff,
  Copy,
  ArrowLeft,
  Subtitles
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface AdminStream {
  id: number;
  title: string;
  description?: string;
  status: 'scheduled' | 'live' | 'ended';
  scheduledStart?: string;
  actualStart?: string;
  endedAt?: string;
  inviteRequired: boolean;
  visibility?: 'public' | 'members' | 'invite_only';
  segmentId?: number;
  segmentName?: string;
  maxViewers?: number;
  currentViewers?: number;
  rtmpsKey?: string;
  rtmpsUrl?: string;
  thumbnailUrl?: string;
  featureImageUrl?: string;
  playbackUrl?: string;
  // Recording/VOD fields
  recordingId?: string;
  recordingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  // Live stream video ID (different from streamUid which is Live Input ID)
  liveVideoId?: string;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  regionRestrictions?: string[];
}

const streamSettingsSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200, 'Title is too long'),
  description: z.string().max(2000, 'Description is too long').optional(),
  thumbnailUrl: z.string().url('Invalid thumbnail URL').optional().or(z.literal('')),
  featureImageUrl: z.string().url('Invalid feature image URL').optional().or(z.literal('')),
  scheduledStart: z.string().optional(),
  visibility: z.enum(['public', 'members', 'invite_only']),
  segmentId: z.number().int().positive().optional(),
  regionRestriction: z.string().optional(),
  tags: z.array(z.string()).optional(),
}).refine((data) => {
  if (data.visibility === 'invite_only') {
    return typeof data.segmentId === 'number' && data.segmentId > 0;
  }
  return true;
}, {
  message: 'Segment is required for invite-only streams',
  path: ['segmentId'],
});

interface SegmentOption {
  id: number;
  name: string;
  memberCount: number;
}

// Converts an ISO date string to a local datetime-local input value (YYYY-MM-DDTHH:MM)
function toLocalDateTimeInput(isoString: string): string {
  const d = new Date(isoString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminStreamDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stream, setStream] = useState<AdminStream | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingKeys, setIsGeneratingKeys] = useState(false);
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [analytics, setAnalytics] = useState<{
    viewerCount: number;
    isLive: boolean;
    analytics?: {
      liveViewers: number;
      websocketViewers?: number;
      totalViews?: number;
      averageWatchTime?: number;
      peakConcurrentViewers?: number;
    };
  } | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'start' | 'end' | null>(null);
  const [captionsActive, setCaptionsActive] = useState(false);
  const [isCaptionsLoading, setIsCaptionsLoading] = useState(false);
  const [segments, setSegments] = useState<SegmentOption[]>([]);

  const form = useForm<z.infer<typeof streamSettingsSchema>>({
    resolver: zodResolver(streamSettingsSchema),
    defaultValues: {
      title: '',
      description: '',
      thumbnailUrl: '',
      featureImageUrl: '',
      scheduledStart: '',
      visibility: 'members' as const,
      segmentId: undefined,
      regionRestriction: undefined,
      tags: [],
    },
  });

  const visibility = form.watch('visibility');

  // Update form when stream data loads
  useEffect(() => {
    if (stream) {
      form.reset({
        title: stream.title || '',
        description: stream.description || '',
        thumbnailUrl: stream.thumbnailUrl || '',
        featureImageUrl: stream.featureImageUrl || '',
        scheduledStart: stream.scheduledStart ? toLocalDateTimeInput(stream.scheduledStart) : '',
        visibility: stream.visibility || (stream.inviteRequired ? 'invite_only' : 'members'),
        segmentId: stream.segmentId,
        regionRestriction: Array.isArray(stream.regionRestrictions)
          ? stream.regionRestrictions[0]
          : undefined,
        tags: Array.isArray(stream.tags) ? stream.tags : [],
      });
    }
  }, [stream, form]);

  useEffect(() => {
    fetchStreamDetails();
  }, [id]);

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await api.get<{ segments: SegmentOption[] }>('/api/admin/segments');
        if (response.success) {
          setSegments(response.data?.segments || []);
        }
      } catch (error) {
        console.error('Failed to fetch segments:', error);
      }
    };

    fetchSegments();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'analytics' && stream?.id) {
      fetchStreamAnalytics();
    }
  }, [activeTab, stream?.id]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const fetchStreamDetails = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await api.get<{ stream: AdminStream }>(`/api/admin/streams/${id}`);

      if (response.success && response.data) {
        setStream(response.data.stream);
      } else {
        toast.error(response.error?.message || 'Failed to load stream details');
      }
    } catch (error) {
      console.error('Failed to fetch stream details:', error);
      toast.error('Failed to load stream details');
    } finally {
      setIsLoading(false);
    }
  };

  const generateStreamKeys = async (streamId: number) => {
    try {
      setIsGeneratingKeys(true);
      const response = await api.post<{ stream: AdminStream }>(`/api/admin/streams/${streamId}/generate-keys`);

      if (response.success && response.data) {
        setStream(response.data.stream);
        toast.success('Stream keys generated successfully');
      } else {
        toast.error(response.error?.message || 'Failed to generate stream keys');
      }
    } catch (error) {
      console.error('Failed to generate stream keys:', error);
      toast.error('Failed to generate stream keys');
    } finally {
      setIsGeneratingKeys(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const startStream = async (streamId: number) => {
    try {
      const response = await api.post<{ message: string; status: string }>(`/api/streams/${streamId}/start`);

      if (response.success) {
        // Refresh stream details to get updated status
        await fetchStreamDetails();
        toast.success('Stream started successfully');
      } else {
        toast.error(response.error?.message || 'Failed to start stream');
      }
    } catch (error) {
      console.error('Failed to start stream:', error);
      toast.error('Failed to start stream');
    }
  };

  const endStream = async (streamId: number) => {
    try {
      const response = await api.post<{ message: string; status: string }>(`/api/streams/${streamId}/end`);

      if (response.success) {
        // Refresh stream details to get updated status
        await fetchStreamDetails();
        toast.success('Stream ended successfully');
      } else {
        toast.error(response.error?.message || 'Failed to end stream');
      }
    } catch (error) {
      console.error('Failed to end stream:', error);
      toast.error('Failed to end stream');
    }
  };

  // Fetch caption status whenever stream loads/changes
  useEffect(() => {
    if (!stream?.id || stream.status !== 'live') return;
    api.get<{ active: boolean }>(`/api/streams/${stream.id}/captions/status`)
      .then(r => { if (r.success && r.data) setCaptionsActive(!!r.data.active); })
      .catch(() => { });
  }, [stream?.id, stream?.status]);

  const startCaptions = async () => {
    if (!stream?.id) return;
    setIsCaptionsLoading(true);
    try {
      const response = await api.post(`/api/streams/${stream.id}/captions/start`);
      if (response.success) {
        setCaptionsActive(true);
        toast.success('Live captions started');
      } else {
        toast.error(response.error?.message || 'Failed to start captions');
      }
    } catch {
      toast.error('Failed to start captions');
    } finally {
      setIsCaptionsLoading(false);
    }
  };

  const stopCaptions = async () => {
    if (!stream?.id) return;
    setIsCaptionsLoading(true);
    try {
      const response = await api.post(`/api/streams/${stream.id}/captions/stop`);
      if (response.success) {
        setCaptionsActive(false);
        toast.success('Live captions stopped');
      } else {
        toast.error(response.error?.message || 'Failed to stop captions');
      }
    } catch {
      toast.error('Failed to stop captions');
    } finally {
      setIsCaptionsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge variant="destructive" className="animate-pulse">Live</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">Scheduled</Badge>;
      case 'ended':
        return <Badge variant="outline">Ended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const fetchStreamAnalytics = async () => {
    if (!stream?.id) return;

    try {
      setIsLoadingAnalytics(true);
      const analyticsData = await dashboardApi.getStreamAnalytics(stream.id);
      if (analyticsData) {
        setAnalytics(analyticsData);
      } else {
        toast.error('Failed to load analytics data');
      }
    } catch (error) {
      console.error('Failed to fetch stream analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleUpdateStream = async (values: z.infer<typeof streamSettingsSchema>) => {
    if (!stream?.id) return;

    try {
      setIsSavingSettings(true);

      const tags = values.tags && values.tags.length > 0 ? values.tags : undefined;

      // Convert datetime-local to ISO string
      const scheduledStart = values.scheduledStart
        ? new Date(values.scheduledStart).toISOString()
        : undefined;

      const response = await api.put(`/api/streams/${stream.id}`, {
        title: values.title,
        description: values.description || undefined,
        thumbnailUrl: values.thumbnailUrl || undefined,
        featureImageUrl: values.featureImageUrl || undefined,
        scheduledStart,
        visibility: values.visibility,
        inviteRequired: values.visibility === 'invite_only',
        segmentId: values.visibility === 'invite_only' ? values.segmentId : undefined,
        regionRestriction: values.regionRestriction || undefined,
        tags,
      });

      if (response.success) {
        toast.success('Stream settings updated successfully');
        // Refresh stream details
        await fetchStreamDetails();
      } else {
        toast.error(response.error?.message || 'Failed to update stream settings');
      }
    } catch (error) {
      console.error('Failed to update stream settings:', error);
      toast.error('Failed to update stream settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!stream) {
    return (
      <AdminLayout>
        <div className="flex items-center gap-4">
          <Button variant="outline" asChild>
            <Link to="/admin/streams">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="p-6 text-center @[1120px]/admin-layout:p-12">
            <p className="text-muted-foreground">Stream not found</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col gap-3 @[900px]/admin-layout:flex-row @[900px]/admin-layout:items-center @[900px]/admin-layout:justify-between">
        <div className="flex flex-wrap items-center gap-3 @[760px]/admin-layout:gap-4">
          <Button variant="outline" asChild>
            <Link to="/admin/streams">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {getStatusBadge(stream.status)}
            {stream.currentViewers !== undefined && (
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />{stream.currentViewers} watching
              </span>
            )}
            {stream.scheduledStart && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />{formatDate(stream.scheduledStart)}
              </span>
            )}
            {stream.inviteRequired && (
              <Badge variant="outline" className="text-xs">Invite Only</Badge>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 @[760px]/admin-layout:flex-row @[760px]/admin-layout:items-center">
          {stream.status === 'scheduled' && (
            <Button className="w-full @[760px]/admin-layout:w-auto" onClick={() => setConfirmAction('start')}>
              <Play className="h-4 w-4 mr-2" />
              Start Stream
            </Button>
          )}
          {stream.status === 'live' && (
            <>
              <Button
                className="w-full @[760px]/admin-layout:w-auto"
                variant={captionsActive ? 'secondary' : 'outline'}
                onClick={captionsActive ? stopCaptions : startCaptions}
                disabled={isCaptionsLoading}
              >
                <Subtitles className="h-4 w-4 mr-2" />
                {isCaptionsLoading ? 'Loading…' : captionsActive ? 'Stop Captions' : 'Start Captions'}
              </Button>
              <Button className="w-full @[760px]/admin-layout:w-auto" variant="destructive" onClick={() => setConfirmAction('end')}>
                <Pause className="h-4 w-4 mr-2" />
                End Stream
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4 @[1120px]/admin-layout:space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 @[900px]/admin-layout:grid-cols-4">
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="overview">Overview</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="access">Access Management</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="analytics">Analytics</TabsTrigger>
          <TabsTrigger className="text-xs @[760px]/admin-layout:text-sm" value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 @[1120px]/admin-layout:space-y-6">
          {/* Stream Player Widget - Always visible */}
          <Card className="w-full">
            <CardHeader>
              <div className="flex flex-col gap-2 @[760px]/admin-layout:flex-row @[760px]/admin-layout:items-center @[760px]/admin-layout:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <Video className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Stream Player</CardTitle>
                  {stream.status === 'live' && (
                    <Badge variant="destructive" className="animate-pulse ml-2">Live</Badge>
                  )}
                </div>
                <Badge variant="outline" className="text-xs capitalize">
                  {stream.status === 'live' ? 'Live Stream' : stream.status === 'ended' ? 'Recorded' : 'Scheduled'}
                </Badge>
              </div>
              <CardDescription>
                {stream.status === 'live'
                  ? 'Watch the live stream playback'
                  : stream.status === 'ended'
                    ? 'View the recorded stream'
                    : 'Stream will be available when it goes live'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stream.playbackUrl || stream.thumbnailUrl ? (
                <StreamPlayer
                  stream={{
                    id: stream.id,
                    title: stream.title,
                    description: stream.description,
                    status: stream.status,
                    createdBy: 0, // Admin doesn't need this for display
                    thumbnailUrl: stream.thumbnailUrl,
                    featureImageUrl: stream.featureImageUrl,
                    playbackUrl: stream.playbackUrl,
                    // Recording/VOD fields
                    recordingId: stream.recordingId,
                    recordingStatus: stream.recordingStatus,
                    // Live stream video ID
                    liveVideoId: stream.liveVideoId,
                    createdAt: stream.createdAt,
                    updatedAt: stream.updatedAt,
                    has_access: true, // Admin always has access
                  }}
                  autoPlay={stream.status === 'live'}
                  className="max-w-full"
                />
              ) : (
                <div className="w-full aspect-video bg-muted rounded-lg flex items-center justify-center border-2 border-dashed">
                  <div className="text-center p-8">
                    <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold mb-2">No Stream Content Available</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {stream.status === 'scheduled'
                        ? 'Stream content will appear here when the stream goes live'
                        : 'No playback URL or thumbnail has been configured for this stream'}
                    </p>
                    {stream.status === 'scheduled' && (
                      <Button onClick={() => setConfirmAction('start')} variant="outline">
                        <Play className="h-4 w-4 mr-2" />
                        Start Stream
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Image Section */}
          {stream.featureImageUrl && (
            <Card>
              <CardHeader>
                <CardTitle>Feature Image</CardTitle>
                <CardDescription>
                  The promotional image displayed for this stream
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative overflow-hidden rounded-lg">
                  <MediaImage
                    src={stream.featureImageUrl}
                    alt={`${stream.title} feature image`}
                    className="h-44 w-full object-cover @[900px]/admin-layout:h-64"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 @[1120px]/admin-layout:grid-cols-2 @[1120px]/admin-layout:gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Stream Information</CardTitle>
                <CardDescription>Basic details about this stream</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="mt-1">{stream.description || 'No description provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="mt-1">{formatDate(stream.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                  <p className="mt-1">{formatDate(stream.updatedAt)}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Streaming Details</CardTitle>
                <CardDescription>Technical information for broadcasting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>RTMP URL</Label>
                  <div className="grid grid-cols-1 gap-2 @[760px]/admin-layout:grid-cols-[1fr_auto]">
                    <Input readOnly value={stream.rtmpsUrl || ''} className="font-mono text-sm bg-muted/50" placeholder="Not configured" />
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(stream.rtmpsUrl || '', 'RTMP URL')}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Stream Key</Label>
                  <div className="grid grid-cols-1 gap-2 @[760px]/admin-layout:grid-cols-[1fr_auto_auto]">
                    <Input
                      type={showStreamKey ? "text" : "password"}
                      readOnly
                      value={stream.rtmpsKey || ''}
                      className="font-mono text-sm bg-muted/50"
                      placeholder="Not configured"
                    />
                    <Button variant="outline" size="icon" onClick={() => setShowStreamKey(!showStreamKey)}>
                      {showStreamKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(stream.rtmpsKey || '', 'Stream Key')}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {(!stream.rtmpsUrl || !stream.rtmpsKey) && (
                  <div className="pt-2">
                    <div className="text-sm text-muted-foreground mb-2">
                      RTMP keys should be auto-generated when the stream is created.
                    </div>
                    <Button
                      onClick={() => generateStreamKeys(stream.id)}
                      disabled={isGeneratingKeys}
                      variant="outline"
                      size="sm"
                    >
                      {isGeneratingKeys ? (
                        <>
                          <LoadingSpinner className="h-4 w-4 mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Video className="h-4 w-4 mr-2" />
                          Generate Keys Manually
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="access">
          <StreamAccessManagement stream={stream} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4 @[1120px]/admin-layout:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Stream Analytics
              </CardTitle>
              <CardDescription>
                Viewership and engagement metrics for this stream
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingAnalytics ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : analytics ? (
                <div className="space-y-4 @[1120px]/admin-layout:space-y-6">
                  {/* Real-time Metrics */}
                  <div
                    className="grid gap-3 @[900px]/admin-layout:gap-4"
                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))' }}
                  >
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center gap-2">
                          <Eye className="h-8 w-8 text-muted-foreground" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-muted-foreground">Current Viewers</p>
                            <p className="text-3xl font-bold">{analytics.viewerCount}</p>
                            {analytics.analytics?.liveViewers !== undefined && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Cloudflare: {analytics.analytics.liveViewers}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {analytics.analytics?.peakConcurrentViewers !== undefined && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex flex-col items-center gap-2">
                            <BarChart3 className="h-8 w-8 text-muted-foreground" />
                            <div className="text-center">
                              <p className="text-sm font-medium text-muted-foreground">Peak Viewers</p>
                              <p className="text-3xl font-bold">{analytics.analytics.peakConcurrentViewers}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {analytics.analytics?.websocketViewers !== undefined && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex flex-col items-center gap-2">
                            <Users className="h-8 w-8 text-muted-foreground" />
                            <div className="text-center">
                              <p className="text-sm font-medium text-muted-foreground">WebSocket Viewers</p>
                              <p className="text-3xl font-bold">{analytics.analytics.websocketViewers}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {analytics.analytics?.totalViews !== undefined && analytics.analytics.totalViews > 0 && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex flex-col items-center gap-2">
                            <Video className="h-8 w-8 text-muted-foreground" />
                            <div className="text-center">
                              <p className="text-sm font-medium text-muted-foreground">Total Views</p>
                              <p className="text-3xl font-bold">{analytics.analytics.totalViews}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {analytics.analytics?.averageWatchTime !== undefined && analytics.analytics.averageWatchTime > 0 && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex flex-col items-center gap-2">
                            <Clock className="h-8 w-8 text-muted-foreground" />
                            <div className="text-center">
                              <p className="text-sm font-medium text-muted-foreground">Avg Watch Time</p>
                              <p className="text-3xl font-bold">
                                {Math.floor(analytics.analytics.averageWatchTime / 60)}m
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Card>
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center gap-2">
                          <div className={`h-8 w-8 rounded-full ${analytics.isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                          <div className="text-center">
                            <p className="text-sm font-medium text-muted-foreground">Stream Status</p>
                            <p className="text-lg font-bold capitalize">{analytics.isLive ? 'Live' : 'Offline'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Refresh Button */}
                  <div className="flex justify-stretch @[760px]/admin-layout:justify-end">
                    <Button className="w-full @[760px]/admin-layout:w-auto" variant="outline" onClick={fetchStreamAnalytics} disabled={isLoadingAnalytics}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Refresh Analytics
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No Analytics Data</h3>
                  <p className="text-muted-foreground mb-4">
                    Analytics data will appear here once the stream is live.
                  </p>
                  <Button variant="outline" onClick={fetchStreamAnalytics} disabled={isLoadingAnalytics}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4 @[1120px]/admin-layout:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Stream Settings
              </CardTitle>
              <CardDescription>
                Configure stream settings and preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleUpdateStream)} className="space-y-4 @[1120px]/admin-layout:space-y-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Stream Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter stream title" {...field} />
                        </FormControl>
                        <FormDescription>
                          The title displayed for this stream
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter stream description"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          A detailed description of what this stream will cover
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 gap-4 @[900px]/admin-layout:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="thumbnailUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Thumbnail</FormLabel>
                          <FormControl>
                            <ImageUploadDropzone
                              value={field.value}
                              onChange={field.onChange}
                              description="JPEG, PNG, WebP · max 5 MB · shown on stream cards"
                              aspectRatio="thumbnail"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="featureImageUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Feature Banner</FormLabel>
                          <FormControl>
                            <ImageUploadDropzone
                              value={field.value}
                              onChange={field.onChange}
                              description="JPEG, PNG, WebP · max 5 MB · banner on stream page"
                              aspectRatio="banner"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="scheduledStart"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Scheduled Start</FormLabel>
                        <FormControl>
                          <Input type="datetime-local" {...field} />
                        </FormControl>
                        <FormDescription>
                          When this stream is scheduled to start
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="visibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Visibility</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select visibility" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="public">Public — Anyone can watch, no login required</SelectItem>
                            <SelectItem value="members">Members Only — Must be signed in</SelectItem>
                            <SelectItem value="invite_only">Invite Only — Must have explicit access</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Controls who can view this stream
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {visibility === 'invite_only' && (
                    <FormField
                      control={form.control}
                      name="segmentId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Segment</FormLabel>
                          <Select
                            value={field.value ? String(field.value) : ''}
                            onValueChange={(value) => field.onChange(parseInt(value, 10))}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a segment" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {segments.map((segment) => (
                                <SelectItem key={segment.id} value={String(segment.id)}>
                                  {segment.name} ({segment.memberCount} members)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Members in this segment will be granted access and synced for this stream.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="regionRestriction"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Region Restriction</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value ?? 'none'}
                            onValueChange={(value) => field.onChange(value === 'none' ? undefined : value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Allow all regions" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Allow all regions</SelectItem>
                              {COUNTRIES.map((country) => (
                                <SelectItem key={country.code} value={country.code}>
                                  {country.name} ({country.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormDescription>
                          Leave unset to allow all regions. If selected, only that country can watch.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <FormControl>
                          <TagsInput
                            value={field.value ?? []}
                            onChange={field.onChange}
                            placeholder="Add tag (Enter to confirm)…"
                          />
                        </FormControl>
                        <FormDescription>
                          Press Enter or comma to add — helps viewers find this stream
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col-reverse gap-2 @[760px]/admin-layout:flex-row @[760px]/admin-layout:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (stream) {
                          form.reset({
                            title: stream.title || '',
                            description: stream.description || '',
                            thumbnailUrl: stream.thumbnailUrl || '',
                            featureImageUrl: stream.featureImageUrl || '',
                            scheduledStart: stream.scheduledStart ? toLocalDateTimeInput(stream.scheduledStart) : '',
                            visibility: stream.visibility || (stream.inviteRequired ? 'invite_only' : 'members'),
                            segmentId: stream.segmentId,
                            regionRestriction: Array.isArray(stream.regionRestrictions)
                              ? stream.regionRestrictions[0]
                              : undefined,
                            tags: Array.isArray(stream.tags) ? stream.tags : [],
                          });
                        }
                      }}
                    >
                      Reset
                    </Button>
                    <Button type="submit" disabled={isSavingSettings}>
                      {isSavingSettings ? (
                        <>
                          <LoadingSpinner className="h-4 w-4 mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Settings className="h-4 w-4 mr-2" />
                          Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Start / End Stream Confirmation Dialogs */}
      <AlertDialog open={confirmAction !== null} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'start' ? 'Go Live?' : 'End Stream?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'start'
                ? 'This will start the stream and make it visible to viewers. Are you ready to go live?'
                : 'This will end the live session for all current viewers. This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction === 'end' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
              onClick={() => {
                if (stream) {
                  if (confirmAction === 'start') startStream(stream.id);
                  else if (confirmAction === 'end') endStream(stream.id);
                }
                setConfirmAction(null);
              }}
            >
              {confirmAction === 'start' ? 'Start Stream' : 'End Stream'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
